import React from 'react';
import {
  Phone,
  Check,
  Plus,
  X,
  AlertTriangle,
} from 'lucide-react';

// Post-bulk-purchase summary. We don't poll trust-status per number in bulk
// mode — the backend runs the full chain synchronously per number, and any
// number listed as `purchased` here already has SHAKEN A-attestation done.
// Reputation touches are fire-and-forget just like the single-purchase path;
// users see them settle in Settings → Phone Numbers.
/**
 * Step 5 (bulk) — per-number batch purchase report.
 * @param {{ bulkResult: { results: Object[], summary: { requested: number, purchased: number, failed: number } }, onGoSettings: () => void, onBuyAnother: () => void }} props
 */
export default function StepBulkSummary({ bulkResult, onGoSettings, onBuyAnother }) {
  const results = bulkResult?.results || [];
  const summary = bulkResult?.summary || { requested: results.length, purchased: 0, failed: 0 };
  const allOK = summary.failed === 0 && summary.purchased === summary.requested;

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-full inline-flex items-center justify-center ${allOK ? 'bg-emerald-500/20' : 'bg-amber-500/20'}`}>
          {allOK ? <Check className="w-5 h-5 text-emerald-300" /> : <AlertTriangle className="w-5 h-5 text-amber-300" />}
        </div>
        <div>
          <div className="font-display font-bold text-lg">
            {allOK
              ? `${summary.purchased} numbers provisioned`
              : `${summary.purchased} of ${summary.requested} purchased`}
          </div>
          <div className="text-xs text-white/55">
            {allOK
              ? 'Each number is attached to your Messaging Service and A-attested.'
              : 'Successful numbers are live; failed ones were auto-released (no charge).'}
          </div>
        </div>
      </div>

      <div className="p-4 rounded-md border border-civix-border/50 bg-black/20 max-h-80 overflow-y-auto">
        <ul className="divide-y divide-civix-border/30">
          {results.map((r) => (
            <li key={r.phoneNumber} className="flex items-center justify-between gap-3 py-2">
              <div className="flex items-center gap-3 min-w-0">
                <Phone className="w-4 h-4 text-civix-purple shrink-0" />
                <span className="font-mono text-sm truncate">{r.e164 || r.phoneNumber}</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {r.status === 'purchased' ? (
                  <span className="px-2 py-0.5 rounded-md text-[11px] border border-emerald-500/40 bg-emerald-500/15 text-emerald-300 inline-flex items-center gap-1">
                    <Check className="w-3 h-3" /> Purchased
                  </span>
                ) : (
                  // Prefer a real reason over the plain “Failed” chip so users
                  // don’t stare at a wall of identical red pills wondering what
                  // happened. Order: mapped code (short, actionable) → error text
                  // → fallback. The full error/hint stays in the title tooltip.
                  <span
                    title={[r.error, r.hint].filter(Boolean).join(' — ') || 'Failed'}
                    className="px-2 py-0.5 rounded-md text-[11px] border border-rose-500/40 bg-rose-500/15 text-rose-300 inline-flex items-center gap-1 max-w-xs truncate"
                  >
                    <X className="w-3 h-3 shrink-0" />
                    <span className="truncate">{r.code ? String(r.code) : (r.error || 'Failed')}</span>
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className="flex flex-wrap gap-3">
        <button onClick={onGoSettings} className="btn-primary inline-flex items-center gap-2">
          <Phone className="w-4 h-4" /> Go to Settings → Phone Numbers
        </button>
        <button onClick={onBuyAnother} className="btn-ghost inline-flex items-center gap-2">
          <Plus className="w-4 h-4" /> Buy more
        </button>
      </div>
    </div>
  );
}
